import { useRef, useState } from "react";
import { ActionIcon, Group, Text, Tooltip } from "@mantine/core";
import { IconThumbDown, IconThumbUp } from "@tabler/icons-react";
import { submitFeedback } from "../api";

/**
 * Compact "was this result useful?" control for one finished image.
 *
 * Two thumbs (up/down) with a highlighted state for the user's own vote.
 * Submission is silent — a failed request just leaves the control unset, so a
 * broken vote can never interrupt the user.
 *
 * Props:
 * - logicalImageId: the result image being rated
 * - withLabel: show the "Was this result useful?" caption
 * - dark: card overlay style (inactive thumbs are dark-filled chips, for use on
 *   top of images/videos)
 * - size: "xs" | "sm" | "md" | "lg"
 */
export default function ResultFeedback({
  logicalImageId,
  withLabel = false,
  dark = false,
  size = "md",
}) {
  const [vote, setVote] = useState(null); // "up" | "down" | null
  const [busy, setBusy] = useState(false);
  const busyRef = useRef(false);

  const pick = async (value) => {
    if (busyRef.current || !logicalImageId) return;
    busyRef.current = true;
    setBusy(true);
    try {
      await submitFeedback(logicalImageId, value);
      setVote(value);
    } catch {
      // silent — ratings are best-effort
    } finally {
      busyRef.current = false;
      setBusy(false);
    }
  };

  const iconSize = size === "lg" ? 20 : 16;
  const inactive = dark
    ? { variant: "filled", color: "dark" }
    : { variant: "subtle", color: "gray" };

  return (
    <Group
      gap={6}
      wrap="nowrap"
      align="center"
      onClick={(e) => e.stopPropagation()}
    >
      {withLabel && (
        <Text fz="xs" c="dimmed" fw={500}>
          Was this result useful?
        </Text>
      )}
      <Tooltip label="Helpful" withArrow>
        <ActionIcon
          aria-label="Helpful"
          variant={vote === "up" ? "filled" : inactive.variant}
          color={vote === "up" ? "green" : inactive.color}
          size={size}
          loading={busy}
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            pick("up");
          }}
          style={{ opacity: vote && vote !== "up" ? 0.45 : 1 }}
        >
          <IconThumbUp size={iconSize} stroke={1.5} />
        </ActionIcon>
      </Tooltip>
      <Tooltip label="Not helpful" withArrow>
        <ActionIcon
          aria-label="Not helpful"
          variant={vote === "down" ? "filled" : inactive.variant}
          color={vote === "down" ? "red" : inactive.color}
          size={size}
          loading={busy}
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            pick("down");
          }}
          style={{ opacity: vote && vote !== "down" ? 0.45 : 1 }}
        >
          <IconThumbDown size={iconSize} stroke={1.5} />
        </ActionIcon>
      </Tooltip>
    </Group>
  );
}
